import React from 'react';
import { Link } from 'react-router-dom';
import SharedLayout from '@/components/SharedLayout';
import SubpageNavigation from '@/components/SubpageNavigation';
import PomodoroGallery from '@/components/PomodoroGallery';
import SEOHead from '@/components/SEOHead';
import { CANONICAL_URLS } from '@/utils/canonicalUrls';

const AestheticPomodoroTimer = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Aesthetic Pomodoro Timer",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "url": CANONICAL_URLS.aestheticPomodoroTimer,
    "description":
      "A free aesthetic Pomodoro timer with beautiful Unsplash backgrounds, lofi-friendly design, and customizable 25-minute focus sessions. Built for study, deep work, and ADHD-friendly focus.",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5",
    },
    "featureList": [
      "Aesthetic Unsplash backgrounds that change with time of day",
      "Cute, minimal, lofi-friendly interface",
      "Customizable 25-minute focus sessions and breaks",
      "Audio chimes and ambient sound options",
      "ADHD-friendly short intervals",
      "Works on desktop, mobile, and tablet",
      "100% free, no sign-up required",
    ],
  };

  return (
    <>
      <SEOHead
        title="Aesthetic Pomodoro Timer Online — Cute, Lofi & Free 2026"
        description="The best aesthetic Pomodoro timer online. Cute, lofi-friendly design with beautiful Unsplash backgrounds. Free 25-minute study timer — no signup, works on any device."
        keywords="aesthetic pomodoro timer, cute pomodoro timer, lofi pomodoro timer, study timer aesthetic, aesthetic study timer, pretty pomodoro timer"
        canonicalUrl={CANONICAL_URLS.aestheticPomodoroTimer}
        schemaData={schema}
      />
      <SharedLayout>
        <article className="text-left px-6 py-12 max-w-4xl mx-auto">
          <SubpageNavigation />

          <header>
            <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">
              Aesthetic Pomodoro Timer — The Prettiest Free Focus Timer Online
            </h1>
            <p className="text-xl mb-6 text-gray-700 dark:text-gray-200">
              Looking for an <strong>aesthetic Pomodoro timer</strong> that actually looks beautiful while you study?
              This is it. Stunning Unsplash backgrounds, a clean lofi-friendly interface, and the proven 25-minute
              focus method — all completely free, with no sign-up.
            </p>
          </header>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">
              Why an Aesthetic Timer Helps You Focus
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-200">
              A clean, beautiful environment quiets visual noise and signals your brain that it's time to focus. Our
              <strong> cute Pomodoro timer</strong> rotates through carefully curated Unsplash photography — sunlit
              beaches, foggy forests, mountain peaks, golden sunsets — so every session feels like a small escape.
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-200">
              <li className="mb-2"><strong>Lofi & study aesthetic</strong> — minimal interface that pairs perfectly with lofi playlists</li>
              <li className="mb-2"><strong>Time-of-day backgrounds</strong> — the imagery shifts from morning to night with your day</li>
              <li className="mb-2"><strong>Cute & calm</strong> — no harsh colors, no clutter, no ads</li>
              <li className="mb-2"><strong>Mobile & desktop</strong> — looks great on phone, tablet, and laptop</li>
            </ul>
          </section>

          <section className="mb-12">
            <PomodoroGallery />
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">
              Perfect for Studying, Work, and Deep Focus
            </h2>
            <p className="mb-4 text-gray-700 dark:text-gray-200">
              Whether you're cramming for finals, writing your thesis, coding, or working from home, an aesthetic
              study timer makes long sessions easier to start and easier to sustain. Pair it with lofi beats and you've
              got the ultimate <strong>study with me</strong> setup.
            </p>
            <p className="mb-4 text-gray-700 dark:text-gray-200">
              Need ADHD-friendly intervals? Try our{' '}
              <Link to="/adhd-pomodoro-timer" className="font-semibold underline">ADHD Pomodoro Timer</Link>. Want the full
              productivity toolkit? Visit the <Link to="/" className="font-semibold underline">homepage</Link> for the
              Eisenhower Matrix and Kanban board too.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Start Your First Aesthetic Pomodoro</h2>
            <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200">
              <li className="mb-2">Open the timer and pick your favorite background</li>
              <li className="mb-2">Hit start and focus on one task for 25 minutes</li>
              <li className="mb-2">Take a 5-minute break — stretch, hydrate, breathe</li>
              <li className="mb-2">After 4 sessions, take a longer 15–30 minute break</li>
            </ol>
            <div className="mt-8">
              <Link
                to="/"
                className="inline-block bg-primary text-primary-foreground font-medium py-3 px-6 rounded-lg shadow-sm hover:opacity-90 transition"
              >
                Start the Aesthetic Timer →
              </Link>
            </div>
          </section>
        </article>
      </SharedLayout>
    </>
  );
};

export default AestheticPomodoroTimer;
